// File: src/components/common/ActingAsSelector.jsx
//
// "Acting as" dropdown — shown in the nav/header for users with the
// 'admin' or 'ServiceProviderAdmin' role.
//
// Both 'admin' and 'ServiceProviderAdmin' use the same endpoint:
//   adminAPI.getImpersonatableInstructors()
// The backend scopes results per caller:
//   - 'admin': all ServiceProvider accounts in the system
//   - 'ServiceProviderAdmin': only ServiceProvider accounts THEY invited
// ServiceProviderAdmin accounts are NEVER returned — impersonation of a
// ServiceProviderAdmin account is not permitted for anyone.
//
// Default selection is always "Myself". While "Myself" is selected, write
// operations on BookingTemplateService / BookingSlotService /
// InstructorConfigService are blocked server-side (403 from
// blockWhenActingAsSelf) — selecting an instructor here is required before
// the instructor's templates/configuration can be managed.

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { UserCog, ChevronDown, Check } from 'lucide-react';
import { adminAPI } from '../../services/api';
import { useActingAs } from '../../context/ActingAsContext';

const AUTH0_ROLES_CLAIM = 'https://booking-slot.com/roles';

const ActingAsSelector = () => {
  const { user, getAccessTokenSilently } = useAuth0();
  const { selectedInstructor, isActingAsSelf, setActingAs } = useActingAs();

  const [instructors, setInstructors] = useState([]);
  const [isLoading,   setIsLoading]   = useState(false);
  const [isOpen,      setIsOpen]      = useState(false);

  const roles = user?.[AUTH0_ROLES_CLAIM] || [];
  const isSystemAdmin         = roles.includes('admin');
  const isServiceProviderAdmin = roles.includes('ServiceProviderAdmin');

  // Only render for delegating roles
  const shouldRender = isSystemAdmin || isServiceProviderAdmin;

  const loadInstructors = useCallback(async () => {
    if (!shouldRender) return;
    setIsLoading(true);
    try {
      // Same endpoint for both 'admin' and 'ServiceProviderAdmin' — the
      // backend scopes results to ServiceProvider accounts each caller
      // manages (admin: all; ServiceProviderAdmin: only their own invites).
      const res = await adminAPI.getImpersonatableInstructors(getAccessTokenSilently);
      if (res.success) setInstructors(res.data || []);
    } catch (err) {
      console.error('Failed to load instructors for Acting As selector:', err);
    } finally {
      setIsLoading(false);
    }
  }, [shouldRender, getAccessTokenSilently]);

  useEffect(() => { loadInstructors(); }, [loadInstructors]);

  if (!shouldRender) return null;

  const handleSelect = (instructor) => {
    if (instructor === null) {
      setActingAs(null); // "Myself"
    } else {
      setActingAs({
        instructorId:   instructor.instructorId,
        instructorName: instructor.instructorName || instructor.instructorEmail || instructor.instructorId
      });
    }
    setIsOpen(false);
    // Reload the page so all currently-loaded data refetches under the new context
    window.location.reload();
  };

  const displayLabel = isActingAsSelf
    ? 'Myself'
    : (selectedInstructor?.instructorName || selectedInstructor?.instructorId);

  return (
    <div style={{ position: 'relative', fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>
      <button
        onClick={() => setIsOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: '0.5rem',
          padding: '0.45rem 0.85rem',
          borderRadius: '0.5rem',
          border: `1.5px solid ${isActingAsSelf ? '#e5e7eb' : '#7c3aed'}`,
          background: isActingAsSelf ? '#fff' : 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)',
          color: isActingAsSelf ? '#374151' : '#6d28d9',
          fontSize: '0.85rem', fontWeight: 600,
          cursor: 'pointer', fontFamily: 'inherit'
        }}
        title={isActingAsSelf
          ? 'Select an instructor to manage their templates and configuration'
          : `Acting as ${displayLabel} — templates and configuration changes apply to this instructor`}
      >
        <UserCog size={15} />
        <span>Acting as: <strong>{displayLabel}</strong></span>
        <ChevronDown size={14} style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
      </button>

      {isOpen && (
        <>
          {/* Click-outside overlay */}
          <div onClick={() => setIsOpen(false)} style={{
            position: 'fixed', inset: 0, zIndex: 40
          }} />

          <div style={{
            position: 'absolute', top: 'calc(100% + 0.4rem)', right: 0,
            minWidth: '260px', maxHeight: '320px', overflowY: 'auto',
            background: '#fff', border: '1.5px solid #e5e7eb',
            borderRadius: '0.6rem', boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            zIndex: 50, padding: '0.4rem'
          }}>
            {/* "Myself" option */}
            <button
              onClick={() => handleSelect(null)}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '0.6rem 0.75rem', borderRadius: '0.4rem', border: 'none',
                background: isActingAsSelf ? '#f5f3ff' : 'transparent',
                color: '#111827', fontSize: '0.85rem', fontWeight: 600,
                cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit'
              }}
            >
              <span>Myself <span style={{ color: '#9ca3af', fontWeight: 400 }}>(manage instructors)</span></span>
              {isActingAsSelf && <Check size={15} color="#7c3aed" />}
            </button>

            <div style={{ borderTop: '1px solid #f3f4f6', margin: '0.3rem 0' }} />

            {isLoading && (
              <div style={{ padding: '0.6rem 0.75rem', fontSize: '0.8rem', color: '#9ca3af' }}>
                Loading instructors…
              </div>
            )}

            {!isLoading && instructors.length === 0 && (
              <div style={{ padding: '0.6rem 0.75rem', fontSize: '0.8rem', color: '#9ca3af' }}>
                No instructors yet. Invite one to get started.
              </div>
            )}

            {!isLoading && instructors.map(inst => {
              const isSelected = selectedInstructor?.instructorId === inst.instructorId;
              return (
                <button
                  key={inst.instructorId}
                  onClick={() => handleSelect(inst)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '0.6rem 0.75rem', borderRadius: '0.4rem', border: 'none',
                    background: isSelected ? '#f5f3ff' : 'transparent',
                    color: '#111827', fontSize: '0.85rem',
                    cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit'
                  }}
                  onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#f9fafb'; }}
                  onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                >
                  <span>
                    <div style={{ fontWeight: 600 }}>{inst.instructorName || inst.instructorEmail}</div>
                    {inst.instructorEmail && inst.instructorName && (
                      <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{inst.instructorEmail}</div>
                    )}
                  </span>
                  {isSelected && <Check size={15} color="#7c3aed" />}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default ActingAsSelector;